/* Career deck — swipeable 'Life'-style career cards with the user's avatar drawn in each uniform. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { ChevronLeft, ChevronRight, Sparkles, Plus, Trash2, Loader2, Wand2 } from "lucide-react";

const money = (n) => `$${Math.round(n / 1000)}k`;

export default function CareerDeck() {
  const [deck, setDeck] = useState(null);
  const [images, setImages] = useState({});
  const [gen, setGen] = useState("");
  const [msg, setMsg] = useState("");
  const [showAvatar, setShowAvatar] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const rail = useRef(null);

  const load = useCallback(() => api.get("/career/cards").then((r) => { setDeck(r.data); setShowAvatar(!r.data.avatar); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!deck) return;
    deck.cards.filter((c) => c.has_image && !images[c.id]).forEach((c) => api.get(`/career/cards/${c.id}/image`).then((r) => r.data.image && setImages((m) => ({ ...m, [c.id]: r.data.image }))).catch(() => {}));
  }, [deck]); // eslint-disable-line

  const generate = async (id) => {
    setGen(id); setMsg("");
    try { const { data } = await api.post(`/career/cards/${id}/image`); setImages((m) => ({ ...m, [id]: data.image })); }
    catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setGen(""); }
  };
  const scroll = (dir) => rail.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  if (!deck) return null;
  return (
    <div className="mt-10" data-testid="career-deck">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gold-deep inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> The game of life · career cards</div>
          <h3 className="font-serif text-2xl sm:text-3xl mt-1">Try on a career. Swipe to the next one.</h3>
          <p className="text-sm text-moss mt-1 max-w-xl">Each card shows you in the uniform and what the job really pays — early years, typical, and top earners. Make your own card for any career, in any outfit you choose.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAvatar(true)} className="inline-flex items-center gap-1.5 border border-ink rounded-full px-3 py-1.5 text-xs font-medium hover:bg-ink hover:text-cream" data-testid="career-deck-avatar-btn"><Wand2 className="w-3.5 h-3.5" /> {deck.avatar ? "Change my look" : "Set my look"}</button>
          <button onClick={() => setShowCustom(true)} className="inline-flex items-center gap-1.5 bg-ink text-cream rounded-full px-3 py-1.5 text-xs font-medium hover:bg-ink-deep" data-testid="career-deck-custom-btn"><Plus className="w-3.5 h-3.5" /> Make my own card</button>
        </div>
      </div>
      {msg && <div className="mt-3 text-xs text-clay" data-testid="career-deck-msg">{msg}</div>}
      {showAvatar && <AvatarForm avatar={deck.avatar} onClose={() => setShowAvatar(false)} onSaved={() => { setImages({}); setShowAvatar(false); load(); }} />}
      {showCustom && <CustomForm onClose={() => setShowCustom(false)} onCreated={(card) => { setShowCustom(false); load().then(() => { generate(card.id); setTimeout(() => rail.current?.scrollTo({ left: 0, behavior: "smooth" }), 300); }); }} />}

      <div className="relative mt-5">
        <button onClick={() => scroll(-1)} className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-cream border border-sand items-center justify-center hover:bg-ink hover:text-cream" data-testid="career-deck-prev" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={() => scroll(1)} className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-cream border border-sand items-center justify-center hover:bg-ink hover:text-cream" data-testid="career-deck-next" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
        <div ref={rail} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1 [scrollbar-width:thin]" data-testid="career-deck-rail">
          {deck.cards.map((c) => <CareerCard key={c.id} c={c} image={images[c.id]} busy={gen === c.id} onGenerate={() => generate(c.id)} onDelete={c.custom ? async () => { await api.delete(`/career/cards/${c.id}`); load(); } : null} />)}
        </div>
      </div>
    </div>
  );
}

function CareerCard({ c, image, busy, onGenerate, onDelete }) {
  return (
    <article className="snap-center shrink-0 w-[300px] rounded-2xl overflow-hidden border border-sand bg-cream shadow-sm" data-testid={`career-card-${c.id}`}>
      <div className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center justify-between ${c.custom ? "bg-gold text-white" : "bg-ink text-cream"}`}>
        <span>{c.custom ? "My card" : "Career card"}</span>
        {onDelete && <button onClick={onDelete} className="hover:text-ink" title="Remove card" data-testid={`career-card-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
      </div>
      <div className="relative aspect-[4/5] bg-linen flex items-center justify-center">
        {image ? <img src={image} alt={`You as a ${c.career}`} className="w-full h-full object-cover" data-testid={`career-card-img-${c.id}`} />
          : (
            <button onClick={onGenerate} disabled={busy} className="flex flex-col items-center gap-2 text-sm text-moss hover:text-ink disabled:opacity-60 px-6 text-center" data-testid={`career-card-generate-${c.id}`}>
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Wand2 className="w-6 h-6 text-gold" />}
              {busy ? "Drawing you in uniform… (~15s)" : <>Tap to see yourself as a<br /><strong className="text-ink">{c.career}</strong></>}
            </button>
          )}
      </div>
      <div className="p-4">
        <h4 className="font-serif text-xl leading-tight" data-testid={`career-card-title-${c.id}`}>{c.career}</h4>
        <div className="text-[11px] text-moss mt-0.5 capitalize">{c.outfit}</div>
        <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
          {[["Starting", c.entry], ["Typical", c.median], ["Top 10%", c.top]].map(([l, v]) => (
            <div key={l} className="rounded-lg bg-linen border border-sand py-2"><div className="text-[9px] uppercase tracking-wider text-sage">{l}</div><div className="font-mono text-sm text-ink">{money(v)}</div></div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-moss"><strong className="text-ink">Time to get there:</strong> {c.years}</div>
        {c.day_in_life && <p className="mt-2 text-[11px] text-moss italic">{c.day_in_life}</p>}
        <ol className="mt-2 space-y-0.5 text-[11px] text-ink list-decimal list-inside">{(c.path || []).map((s, i) => <li key={i}>{s}</li>)}</ol>
      </div>
    </article>
  );
}

function AvatarForm({ avatar, onClose, onSaved }) {
  const [style, setStyle] = useState(avatar?.style || "cartoon");
  const [desc, setDesc] = useState(avatar?.description || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/career/avatar", { style, description: desc }); onSaved(); }
    catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-4 bg-linen border border-sand rounded-2xl p-5" data-testid="career-avatar-form">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">How should the cards draw you?</div>
      <div className="mt-3 flex gap-2">
        {[["cartoon", "Cartoon"], ["painted", "Painted portrait"]].map(([k, l]) => (
          <button type="button" key={k} onClick={() => setStyle(k)} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${style === k ? "bg-ink text-cream border-ink" : "bg-white border-sand"}`} data-testid={`career-avatar-style-${k}`}>{l}</button>
        ))}
      </div>
      <input value={desc} onChange={(e) => setDesc(e.target.value)} required minLength={3} maxLength={200} placeholder="Describe yourself — e.g. Black woman, 30s, long braids, glasses, warm smile" className="mt-3 w-full bg-white border border-sand rounded-lg px-3 py-2.5 text-sm" data-testid="career-avatar-desc" />
      <div className="mt-3 flex items-center gap-3">
        <button type="submit" disabled={busy} className="bg-ink text-cream rounded-lg px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid="career-avatar-save">{busy ? "Saving…" : "Save my look"}</button>
        <button type="button" onClick={onClose} className="text-xs text-sage hover:text-ink" data-testid="career-avatar-cancel">Cancel</button>
        {err && <span className="text-xs text-clay">{err}</span>}
      </div>
    </form>
  );
}

function CustomForm({ onClose, onCreated }) {
  const [career, setCareer] = useState("");
  const [outfit, setOutfit] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/career/cards", { career, outfit }); onCreated(data); }
    catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 bg-gold/10 border border-gold/40 rounded-2xl p-5" data-testid="career-custom-form">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gold-deep">Make my own card</div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <input value={career} onChange={(e) => setCareer(e.target.value)} required maxLength={60} placeholder="Career — e.g. Singer" className="bg-white border border-sand rounded-lg px-3 py-2.5 text-sm" data-testid="career-custom-career" />
        <input value={outfit} onChange={(e) => setOutfit(e.target.value)} required maxLength={160} placeholder="Outfit — e.g. sequined gold gown, mic in hand" className="bg-white border border-sand rounded-lg px-3 py-2.5 text-sm" data-testid="career-custom-outfit" />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button type="submit" disabled={busy} className="bg-gold text-white rounded-lg px-4 py-2 text-xs font-medium disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="career-custom-submit">{busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Researching pay…</> : "Create card"}</button>
        <button type="button" onClick={onClose} className="text-xs text-sage hover:text-ink" data-testid="career-custom-cancel">Cancel</button>
        {err && <span className="text-xs text-clay">{err}</span>}
      </div>
    </form>
  );
}
